import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import type { ChatThreadWithMessages } from '@/lib/chats/chat-utils';
import { formatMessagePreview } from '@/lib/chats/chat-utils';
import type { RawChatMessage, GetRoomMessagesResponse } from '@/services/chats/types';
import { useAuthStore } from '@/stores/auth-store';
import { useChatOutboxStore } from '@/stores/chat-outbox-store';
import { queryClient } from '@/lib/query/client';
import { queryKeys } from '@/lib/query';

/**
 * Chat store state interface.
 * 
 * Manages chat-related global state including:
 * - Currently selected chat thread
 * - Typing indicators for users in each thread
 * - Online status of users
 * - Cached chat threads with messages (stored as array for direct access)
 */
/** A pending reply staged in a thread's composer. */
export interface ReplyDraft {
  /** Server message id (`id` field on RawChatMessage) of the message being replied to */
  messageId: number;
  /** Display name to show in the banner ("You" / sender_name) */
  senderName: string;
  /** Short preview text of the parent (already shortened) */
  snippet: string;
}

interface ChatState {
  /** Currently selected chat thread ID (null if no chat is selected) */
  selectedThread: string | null;

  /** Map of thread ID (room_id from API) to Set of user IDs who are currently typing in that thread */
  typingUsers: Map<string, Set<number>>;

  /** Map of user_id to online status (true = online, false = offline) */
  onlineUsers: Map<number, boolean>;

  /** Map of user_id to last-seen timestamp in ms. Set when the user goes offline, or from the server's `last_seen_time` in bulk presence events. */
  lastSeenMap: Map<number, number>;

  /** Map of thread_id → pending reply draft. One draft max per thread. */
  replyDrafts: Map<string, ReplyDraft>;

  /** Array of cached chat threads with messages, sorted by most recent message (newest first) */
  threads: ChatThreadWithMessages[];

  /** Whether chat data is currently being hydrated (loading initial data) */
  isHydrating: boolean;

  /**
   * Next chat-list page number to fetch when the user scrolls past the
   * currently-loaded threads. The initial poll covers page 1; pages 2..N are
   * lazily loaded by `useThreadListBackfill` only when the user scrolls near
   * the bottom of the sidebar. Loading everything upfront pushed the heavy
   * account past 1 GB before any interaction.
   */
  threadsNextPage: number;

  /** Whether the server may still have older chat-list pages to load. */
  threadsHasMore: boolean;

  /** True while an older chat-list page is currently being fetched. */
  threadsLoadingMore: boolean;

  /** Update the lazy-load pagination state. Merge semantics. */
  setThreadsPagination: (
    next: Partial<{ nextPage: number; hasMore: boolean; loadingMore: boolean }>,
  ) => void;

  /** Set the currently selected chat thread */
  setSelectedThread: (threadId: string | null) => void;
  
  /** Update typing status for a user in a specific thread */
  setTypingUser: (threadId: string, userId: number, isTyping: boolean) => void;
  
  /** Get array of user IDs who are currently typing in a thread */
  getTypingUsers: (threadId: string) => number[];
  
  /** Update online status for a user */
  setUserOnlineStatus: (userId: number, isOnline: boolean) => void;
  
  /** Get online status for a user (returns undefined if status is unknown) */
  getUserOnlineStatus: (userId: number) => boolean | undefined;

  /** Record a user's last-seen timestamp (ms since epoch) */
  setUserLastSeen: (userId: number, timestamp: number) => void;
  
  /** Set all threads (replaces entire array) */
  setThreads: (threads: ChatThreadWithMessages[]) => void;

  /**
   * Insert a thread if not already present (does NOT overwrite an existing
   * thread). Used to seed an empty thread when the user starts a new chat with
   * a contact they have not messaged before.
   */
  upsertThread: (thread: ChatThreadWithMessages) => void;

  /** Remove a thread from the local cache by room_id. */
  removeThread: (threadId: string) => void;

  /**
   * Zero a thread's unread badge once the user has actually read it.
   *
   * `meta.unread_count` is computed server-side-ish by
   * `transformMessagesToThreads` on a FULL sync only; read receipts update
   * `message_status` but never the counter, so without this the badge survives
   * until the next full sync.
   */
  markThreadRead: (threadId: string) => void;

  /** Clear all messages from a thread but keep the thread in the list. */
  clearThreadMessages: (threadId: string) => void;
  
  /** Retrieve a cached chat thread by thread ID (room_id from API) (returns undefined if not cached) */
  getThread: (threadId: string) => ChatThreadWithMessages | undefined;
  
  /**
   * Insert a message at the end of a thread.
   *
   * @param threadId - Thread ID (room_id from API) to add message to
   * @param message - Message to insert
   * @returns true if operation succeeded, false if thread not found
   */
  insertMsg: (threadId: string, message: RawChatMessage) => boolean;

  /**
   * Batch variant of {@link insertMsg} — inserts many messages into a thread in
   * ONE store update. Used by the socket-ingest coalescer so a group flood
   * becomes a single render instead of N. Unlike insertMsg it does NOT mirror
   * into the room-messages query cache: that write would re-fire the
   * `pages → mergeMessages` sync and re-create every message object, which both
   * churns and breaks `MessageRow` memoization. Existing message objects keep
   * their identity (only genuinely-new messages are new refs). Returns true if
   * any message was added.
   */
  insertMsgs: (threadId: string, messages: RawChatMessage[]) => boolean;

  /**
   * Bulk-merge a batch of messages into a thread (history sync). Unlike
   * insertMsg this dedupes by id, preserves chronological order, and is
   * safe to call with hundreds of records.
   *
   * @param threadId - Thread ID (room_id from API)
   * @param messages - Messages to merge (any order)
   * @returns true if any new messages were added, false otherwise
   */
  mergeMessages: (threadId: string, messages: RawChatMessage[]) => boolean;
  
  /**
   * Delete a message from a thread by message ID.
   * 
   * @param threadId - Thread ID (room_id from API) containing the message
   * @param messageId - Message ID (id field from RawChatMessage) to delete
   * @returns true if message was found and deleted, false otherwise
   */
  deleteMsg: (threadId: string, messageId: number) => boolean;
  
  /**
   * Update a message in a thread (used for optimistic updates).
   * Finds message by unique_message_id and updates it.
   *
   * @param threadId - Thread ID (room_id from API) containing the message
   * @param uniqueMessageId - Unique message ID to find
   * @param updates - Partial message updates to apply
   * @returns true if message was found and updated, false otherwise
   */
  updateMsg: (threadId: string, uniqueMessageId: string, updates: Partial<RawChatMessage>) => boolean;

  /**
   * Set (or clear) a user's reaction on a single message.
   *
   * Walks `message_status` for the given chat id and updates the entry
   * matching `userId`. Used by both the optimistic-send path and the
   * incoming `chat_reaction_status_all` socket event.
   *
   * @param threadId   - Thread the message lives in
   * @param chatId     - Server message id (`id` field on RawChatMessage)
   * @param userId     - Reactor's user id
   * @param reaction   - Emoji to set; pass `null` or empty string to clear
   * @returns true if the message + status entry were found and updated
   */
  setReaction: (
    threadId: string,
    chatId: number,
    userId: number,
    reaction: string | null,
  ) => boolean;

  /**
   * Mark a single message as delivered / read for one recipient.
   *
   * Scans every thread for the chat id (sender's view rarely knows which
   * thread the message lives in by the time the ack arrives), then updates
   * the `message_status` entry for `userId`. Synthesizes a new row when the
   * server-side per-user record hasn't been seeded yet (typical in groups).
   * Returns true when the message + status entry were found and updated.
   */
  setMessageStatus: (
    chatId: number,
    userId: number,
    fields: { received_time?: string | number; read_time?: string | number },
  ) => boolean;

  /**
   * Batch variant of {@link setMessageStatus}. Applies many delivery/read
   * receipts in a SINGLE store update — the `chat_*_status_all` events fire a
   * receipt per message on thread-open, and applying them one-by-one rebuilt
   * the whole thread (and previously the entire room-query cache) once per
   * message, which is what spiked memory to OOM. Returns true if anything
   * actually changed.
   */
  setMessageStatuses: (
    updates: Array<{
      chatId: number;
      userId: number;
      received_time?: string | number;
      read_time?: string | number;
    }>,
  ) => boolean;

  /** Set hydration state (true when hydrating, false when done) */
  setIsHydrating: (isHydrating: boolean) => void;

  /** Stage a reply draft for the given thread (replaces any existing draft). */
  setReplyDraft: (threadId: string, draft: ReplyDraft) => void;

  /** Clear the reply draft for a thread (no-op if absent). */
  clearReplyDraft: (threadId: string) => void;

  /** Clear all chat state (reset to initial state) */
  clearChat: () => void;
}

/**
 * Zustand store for managing chat-related global state.
 * 
 * This store provides:
 * - **Thread caching**: Stores chat threads with messages as an array for direct access
 * - **Typing indicators**: Tracks which users are typing in which threads
 * - **Online status**: Tracks which users are currently online
 * - **Selected chat**: Tracks which chat thread is currently being viewed
 * 
 * @example
 * ```typescript
 * // Get all threads (already an array)
 * const { threads } = useChatStore();
 * threads.map(thread => ...)
 * 
 * // Get a specific thread
 * const { getThread } = useChatStore();
 * const thread = getThread('chat_123');
 * 
 * // Update typing status
 * const { setTypingUser } = useChatStore();
 * setTypingUser('chat_123', 456, true); // User 456 is typing in chat_123
 * 
 * // Check if user is online
 * const { getUserOnlineStatus } = useChatStore();
 * const isOnline = getUserOnlineStatus(456);
 * ```
 * 
 * @remarks
 * - Threads are stored as an array for direct access in components
 * - Threads are automatically sorted by most recent message when set
 * - Typing status should be updated via WebSocket events
 * - Online status should be updated via WebSocket presence events
 * - The store uses immutable updates for proper React reactivity
 */
/** Key under which the chat store is persisted in IndexedDB. */
export const CHAT_STORE_PERSIST_KEY = 'chat-store';

/**
 * Max messages kept per thread in the offline cache. Matches the room-message
 * page size (`useRoomMessages`) so a reload + first-page fetch line up.
 */
const MAX_PERSISTED_MESSAGES = 30;

/**
 * Max messages kept per thread in RAM. Heavy accounts pushed unbounded arrays
 * into the store and OOM'd the renderer; capping bounds the per-thread memory
 * footprint regardless of how active a conversation is.
 *
 * This is the store's fallback cache, NOT the open-thread render source: the
 * open thread renders from the `useRoomMessages` infinite-query sliding window
 * (`WINDOW_MAX_PAGES × PAGE_LIMIT` = 25 × 30 = 750), so the two are decoupled
 * and this no longer needs to match the scroll-back window. The store keeps the
 * newest 150 per thread as a cheap cache/preview source (~a few hundred
 * KB/thread); older history lives in the query cache and is re-fetched on
 * scroll-back.
 */
const MAX_THREAD_MESSAGES = 150;

/** IndexedDB-backed storage adapter for Zustand's persist middleware. */
const idbStateStorage: StateStorage = {
  getItem: async (name) => (await idbGet<string>(name)) ?? null,
  setItem: async (name, value) => {
    await idbSet(name, value);
  },
  removeItem: async (name) => {
    await idbDel(name);
  },
};

/* ------------------------------------------------------------------ *
 * Room-messages infinite-query cache sync
 *
 * Messages are also held in a persisted `useInfiniteQuery` per room
 * (see `useRoomMessages`). The store mutation methods below are the single
 * point every live event (socket message, optimistic send, reaction,
 * delete) converges through — so patching the query cache here keeps the
 * persisted IndexedDB history consistent without rewiring every caller.
 *
 * `mergeMessages` is intentionally NOT synced: it is the query → store
 * direction, and writing back to the query from it would loop.
 * ------------------------------------------------------------------ */

/** Shape of the cached data for a room's `useInfiniteQuery`. */
type RoomMessagesQueryData = {
  pages: (GetRoomMessagesResponse | null)[];
  pageParams: unknown[];
};

/** Resolve the current user's id for building the room-messages query key. */
function currentUserId(): number {
  const u = useAuthStore.getState().user;
  return u?.id ?? u?.user_id ?? 0;
}

/**
 * Apply a per-message transform across every page of a room's cached
 * infinite query. Returning `null` from `mapFn` drops the message.
 * No-op when the room's query has never been fetched.
 */
function mutateRoomQueryMessages(
  roomId: string,
  mapFn: (m: RawChatMessage) => RawChatMessage | null,
): void {
  const key = queryKeys.chat.messages({ userId: currentUserId(), roomId });
  queryClient.setQueryData<RoomMessagesQueryData>(key, (old) => {
    if (!old?.pages?.length) return old;
    const pages = old.pages.map((page) => {
      if (!page) return page;
      const data = (page.data ?? [])
        .map(mapFn)
        .filter((m): m is RawChatMessage => m !== null);
      return { ...page, data };
    });
    return { ...old, pages };
  });
}

/**
 * Prepend a brand-new message to a room's cached infinite query (newest
 * page). Skips messages already cached — a socket echo of an optimistic
 * send is reconciled by `updateMsg` instead. No-op when the room's query
 * has never been fetched (opening the thread fetches fresh from server).
 */
function prependToRoomQuery(roomId: string, message: RawChatMessage): void {
  const key = queryKeys.chat.messages({ userId: currentUserId(), roomId });
  queryClient.setQueryData<RoomMessagesQueryData>(key, (old) => {
    if (!old?.pages?.length) return old;
    const exists = old.pages.some((page) =>
      (page?.data ?? []).some(
        (m) =>
          (message.id > 0 && m.id === message.id) ||
          (!!message.unique_message_id &&
            m.unique_message_id === message.unique_message_id),
      ),
    );
    if (exists) return old;
    const [first, ...rest] = old.pages;
    const newFirst = first
      ? { ...first, data: [message, ...(first.data ?? [])] }
      : first;
    return { ...old, pages: [newFirst, ...rest] };
  });
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
  selectedThread: null,
  typingUsers: new Map(),
  onlineUsers: new Map(),
  lastSeenMap: new Map(),
  replyDrafts: new Map(),
  threads: [],
  isHydrating: false,
  threadsNextPage: 2,
  threadsHasMore: true,
  threadsLoadingMore: false,

  setThreadsPagination: (next) => {
    const cur = get();
    set({
      threadsNextPage: next.nextPage ?? cur.threadsNextPage,
      threadsHasMore: next.hasMore ?? cur.threadsHasMore,
      threadsLoadingMore: next.loadingMore ?? cur.threadsLoadingMore,
    });
  },
  
  /**
   * Set the currently selected chat thread.
   * Used to track which chat window is active.
   * 
   * @param threadId - Thread ID of the selected chat, or null to deselect
   */
  setSelectedThread: (threadId) => set({ selectedThread: threadId }),
  
  /**
   * Update typing status for a user in a specific thread.
   * 
   * @param threadId - Thread ID (room_id from API) where the user is typing
   * @param userId - User ID of the typing user
   * @param isTyping - true if user started typing, false if user stopped typing
   * 
   * @example
   * ```typescript
   * setTypingUser('chat_123', 456, true);  // User 456 started typing
   * setTypingUser('chat_123', 456, false); // User 456 stopped typing
   * ```
   */
  setTypingUser: (threadId, userId, isTyping) => {
    const { typingUsers } = get();
    const newTypingUsers = new Map(typingUsers);
    
    // Get existing Set or create new one
    const existingSet = newTypingUsers.get(threadId);
    const newSet = existingSet ? new Set(existingSet) : new Set<number>();
    
    if (isTyping) {
      newSet.add(userId);
    } else {
      newSet.delete(userId);
    }
    
    // Always set the new Set (even if empty) to ensure reference changes
    newTypingUsers.set(threadId, newSet);
    
    set({ typingUsers: newTypingUsers });
  },
  
  /**
   * Get array of user IDs who are currently typing in a thread.
   * 
   * @param threadId - Thread ID (room_id from API) to check for typing users
   * @returns Array of user IDs who are typing, or empty array if none
   * 
   * @example
   * ```typescript
   * const typingUsers = getTypingUsers('chat_123');
   * // Returns: [456, 789] if users 456 and 789 are typing
   * ```
   */
  getTypingUsers: (threadId) => {
    const { typingUsers } = get();
    return Array.from(typingUsers.get(threadId) || []);
  },
  
  /**
   * Update online status for a user.
   * 
   * @param userId - User ID to update
   * @param isOnline - true if user is online, false if offline
   * 
   * @example
   * ```typescript
   * setUserOnlineStatus(456, true);  // User 456 is online
   * setUserOnlineStatus(456, false); // User 456 is offline
   * ```
   */
  setUserOnlineStatus: (userId, isOnline) => {
    const { onlineUsers } = get();
    const newOnlineUsers = new Map(onlineUsers);
    newOnlineUsers.set(userId, isOnline);
    set({ onlineUsers: newOnlineUsers });
  },
  
  /**
   * Get online status for a user.
   * 
   * @param userId - User ID to check
   * @returns true if online, false if offline, undefined if status is unknown
   * 
   * @example
   * ```typescript
   * const isOnline = getUserOnlineStatus(456);
   * if (isOnline === true) {
   *   // User is online
   * }
   * ```
   */
  getUserOnlineStatus: (userId) => {
    const { onlineUsers } = get();
    return onlineUsers.get(userId);
  },

  setUserLastSeen: (userId, timestamp) => {
    const { lastSeenMap } = get();
    const newMap = new Map(lastSeenMap);
    newMap.set(userId, timestamp);
    set({ lastSeenMap: newMap });
  },
  
  /**
   * Set all threads at once (replaces entire array).
   * 
   * Used when loading chat list to set all threads at once.
   * Threads are automatically sorted by most recent message.
   * 
   * @param threads - Array of threads to set
   * 
   * @example
   * ```typescript
   * setThreads([thread1, thread2, thread3]);
   * ```
   */
  setThreads: (threads) => {
    // Enforce the in-memory message cap on every thread before storing.
    // This is the single chokepoint for bulk-thread writes — the chat-list
    // full sync, the 50-page backfill, and the group-reconcile pass all end
    // here. Without the cap, a heavy account's first-load fan-out can push
    // tens of thousands of messages into the store and OOM the renderer
    // before any per-message mutator (insertMsg/mergeMessages) runs.
    const bounded = threads.map((t) =>
      t.messages.length > MAX_THREAD_MESSAGES
        ? { ...t, messages: t.messages.slice(0, MAX_THREAD_MESSAGES) }
        : t,
    );
    set({ threads: bounded });
  },

  upsertThread: (thread) => {
    const { threads } = get();
    if (threads.some((t) => t.meta.room_id === thread.meta.room_id)) return;
    set({ threads: [thread, ...threads] });
  },

  removeThread: (threadId) => {
    const { threads } = get();
    const next = threads.filter((t) => t.meta.room_id !== threadId);
    if (next.length === threads.length) return;
    set({ threads: next });
  },

  markThreadRead: (threadId) => {
    const { threads } = get();
    const idx = threads.findIndex((t) => t.meta.room_id === threadId);
    if (idx === -1) return;
    if (threads[idx].meta.unread_count === 0) return; // already clear — no re-render
    const next = [...threads];
    next[idx] = {
      ...next[idx],
      meta: { ...next[idx].meta, unread_count: 0 },
    };
    set({ threads: next });
  },

  clearThreadMessages: (threadId) => {
    const { threads } = get();
    const idx = threads.findIndex((t) => t.meta.room_id === threadId);
    if (idx === -1) return;
    const next = [...threads];
    next[idx] = {
      ...next[idx],
      meta: { ...next[idx].meta, preview_text: '' },
      messages: [],
    };
    set({ threads: next });
    // Drop every message from the persisted room-messages query cache too.
    mutateRoomQueryMessages(threadId, () => null);
  },

  /**
   * Retrieve a cached chat thread by thread ID.
   * 
   * Used by chat window route to get messages without refetching.
   * Returns undefined if thread is not in cache (e.g., direct navigation or page refresh).
   * 
   * @param threadId - Thread ID (room_id from API, e.g., 'chat_123' or 'group_456')
   * @returns Thread with messages if cached, undefined otherwise
   * 
   * @example
   * ```typescript
   * const thread = getThread('chat_123');
   * if (thread) {
   *   // Thread is cached, can display messages immediately
   *   console.log(thread.messages);
   * } else {
   *   // Thread not cached, need to fetch
   * }
   * ```
   */
  getThread: (threadId) => {
    const { threads } = get();
    return threads.find(t => t.meta.room_id === threadId);
  },
  
  /**
   * Insert a message at the end of a thread.
   * 
   * Adds a new message to the end of the thread's messages array.
   * This is useful for real-time message updates when new messages arrive.
   * 
   * @param threadId - Thread ID (room_id from API) to add message to
   * @param message - Message to insert
   * @returns true if operation succeeded, false if thread not found
   * 
   * @example
   * ```typescript
   * insertMsg('chat_123', newMessage);
   * ```
   */
  insertMsg: (threadId, message) => {
    const { threads } = get();
    const threadIndex = threads.findIndex(t => t.meta.room_id === threadId);

    if (threadIndex === -1) {
      return false; // Thread not found
    }

    const existingThread = threads[threadIndex];

    // Normalize message: ensure it has room_id and a sane created_time.
    // Socket payloads sometimes arrive with roomId (camelCase) or without
    // created_time at all — without created_time, groupMessagesByDate in the
    // chat window drops the message, so the sidebar preview updates but the
    // bubble never renders.
    const normalizedMessage = {
      ...message,
      room_id: threadId,
      created_time:
        Number(message.created_time) > 0 ? Number(message.created_time) : Date.now(),
    };
    
    // Update preview text with the new message. Pass currentUserId so a
    // group system event addressed to me reads "X added you" instead of
    // "X added <my-display-name>".
    const authUser = useAuthStore.getState().user;
    const currentUserId = authUser?.id ?? authUser?.user_id;
    const previewText = formatMessagePreview(normalizedMessage, currentUserId);
    
    // Cap the in-memory message window so heavy threads don't grow without
    // bound. Messages are stored newest-first, so slicing the head keeps the
    // most recent N — older history is still in the room-messages
    // infinite-query cache and is re-fetched on scroll-back.
    const nextMessages = [normalizedMessage, ...existingThread.messages].slice(
      0,
      MAX_THREAD_MESSAGES,
    );
    // A live incoming message bumps the sidebar badge unless the user is
    // sitting in that thread (where it is read on arrival). Without this the
    // counter only ever moves on a full sync.
    const countsAsUnread =
      normalizedMessage.from_user_id !== currentUserId &&
      get().selectedThread !== threadId;

    const updatedThread: ChatThreadWithMessages = {
      ...existingThread,
      meta: {
        ...existingThread.meta,
        preview_text: previewText,
        unread_count: countsAsUnread
          ? existingThread.meta.unread_count + 1
          : existingThread.meta.unread_count,
      },
      messages: nextMessages,
    };

    // Move the updated thread to the top of the list so the chat that just
    // received a message bubbles up (WhatsApp/Telegram behaviour), instead of
    // staying at its old index until the page is reloaded.
    const newThreads = [
      updatedThread,
      ...threads.slice(0, threadIndex),
      ...threads.slice(threadIndex + 1),
    ];

    set({ threads: newThreads });

    // Keep the persisted room-messages infinite-query cache in sync.
    prependToRoomQuery(threadId, normalizedMessage);

    return true;
  },

  insertMsgs: (threadId, messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return false;

    const { threads } = get();
    const threadIndex = threads.findIndex((t) => t.meta.room_id === threadId);
    if (threadIndex === -1) return false;

    const existingThread = threads[threadIndex];

    // Dedupe by server id (positive) else unique_message_id. Seed the map with
    // the EXISTING messages by reference so unchanged bubbles keep their object
    // identity (MessageRow memo relies on this).
    const keyOf = (m: RawChatMessage): string =>
      m.id && m.id > 0
        ? `id:${m.id}`
        : `uid:${m.unique_message_id ?? `${m.created_time}-${m.from_user_id}`}`;

    const byKey = new Map<string, RawChatMessage>();
    for (const m of existingThread.messages) byKey.set(keyOf(m), m);

    const freshlyAdded: RawChatMessage[] = [];
    for (const raw of messages) {
      const normalized: RawChatMessage = {
        ...raw,
        room_id: threadId,
        created_time:
          Number(raw.created_time) > 0 ? Number(raw.created_time) : Date.now(),
      };
      const k = keyOf(normalized);
      if (byKey.has(k)) continue; // duplicate echo — keep existing reference
      byKey.set(k, normalized);
      freshlyAdded.push(normalized);
    }
    if (freshlyAdded.length === 0) return false;

    const merged = Array.from(byKey.values())
      .sort((a, b) => Number(b.created_time) - Number(a.created_time))
      .slice(0, MAX_THREAD_MESSAGES);

    const newestMessage = merged[0];
    const authUser = useAuthStore.getState().user;
    const currentUserId = authUser?.id ?? authUser?.user_id;
    const previewText = newestMessage
      ? formatMessagePreview(newestMessage, currentUserId)
      : existingThread.meta.preview_text;

    // Same badge rule as insertMsg, applied to this batch's genuinely-new
    // incoming messages (echoes of our own sends never count).
    const newUnread =
      get().selectedThread === threadId
        ? 0
        : freshlyAdded.filter((m) => m.from_user_id !== currentUserId).length;

    const updatedThread: ChatThreadWithMessages = {
      ...existingThread,
      meta: {
        ...existingThread.meta,
        preview_text: previewText,
        unread_count: existingThread.meta.unread_count + newUnread,
      },
      messages: merged,
    };

    // Bubble the thread to the top (WhatsApp/Telegram behaviour), same as insertMsg.
    const newThreads = [
      updatedThread,
      ...threads.slice(0, threadIndex),
      ...threads.slice(threadIndex + 1),
    ];
    set({ threads: newThreads });

    // Mirror the new messages into the room-messages query window — that window
    // is the open thread's display source, so live arrivals must land there too.
    // Bounded cost: the window holds at most WINDOW_MAX_PAGES pages.
    for (const m of freshlyAdded) prependToRoomQuery(threadId, m);

    return true;
  },

  mergeMessages: (threadId, messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return false;

    const { threads } = get();
    const threadIndex = threads.findIndex((t) => t.meta.room_id === threadId);
    if (threadIndex === -1) {
      return false;
    }

    const existingThread = threads[threadIndex];

    // Normalize incoming records the same way insertMsg does so socket
    // payloads with camelCase or missing created_time don't break grouping.
    const normalize = (m: RawChatMessage): RawChatMessage => ({
      ...m,
      room_id: threadId,
      created_time:
        Number(m.created_time) > 0 ? Number(m.created_time) : Date.now(),
    });

    // Dedup by id (server-assigned positive id). For optimistic messages
    // (negative id, no server entry yet) we fall back to unique_message_id.
    const keyOf = (m: RawChatMessage): string | number =>
      m.id && m.id > 0 ? m.id : (m.unique_message_id || `${m.created_time}-${m.from_user_id}`);

    const byKey = new Map<string | number, RawChatMessage>();
    for (const m of existingThread.messages) byKey.set(keyOf(m), m);

    let added = 0;
    for (const raw of messages) {
      const m = normalize(raw);
      const k = keyOf(m);
      if (byKey.has(k)) {
        // Prefer the incoming record so server-side updates (read receipts,
        // edits) overwrite the cached copy.
        byKey.set(k, m);
      } else {
        byKey.set(k, m);
        added++;
      }
    }

    const merged = Array.from(byKey.values())
      .sort((a, b) => Number(b.created_time) - Number(a.created_time))
      .slice(0, MAX_THREAD_MESSAGES);

    const newestMessage = merged[0];
    const authUser = useAuthStore.getState().user;
    const currentUserId = authUser?.id ?? authUser?.user_id;
    const previewText = newestMessage
      ? formatMessagePreview(newestMessage, currentUserId)
      : existingThread.meta.preview_text;

    const updatedThread: ChatThreadWithMessages = {
      ...existingThread,
      meta: {
        ...existingThread.meta,
        preview_text: previewText,
      },
      messages: merged,
    };

    const newThreads = [...threads];
    newThreads[threadIndex] = updatedThread;
    set({ threads: newThreads });

    return added > 0;
  },

  /**
   * Delete a message from a thread by message ID.
   * 
   * Removes the message with the given ID from the specified thread's messages array.
   * 
   * @param threadId - Thread ID (room_id from API) containing the message
   * @param messageId - Message ID (id field from RawChatMessage) to delete
   * @returns true if message was found and deleted, false if thread not found or message not found
   * 
   * @example
   * ```typescript
   * deleteMsg('chat_123', 12345); // Deletes message with id: 12345 from thread 'chat_123'
   * ```
   */
  deleteMsg: (threadId, messageId) => {
    const { threads } = get();
    const threadIndex = threads.findIndex(t => t.meta.room_id === threadId);
    
    if (threadIndex === -1) {
      return false; // Thread not found
    }
    
    const existingThread = threads[threadIndex];
    const messageIndex = existingThread.messages.findIndex(msg => msg.id === messageId);
    
    if (messageIndex === -1) {
      return false; // Message not found in thread
    }
    
    const updatedMessages = [...existingThread.messages];
    updatedMessages.splice(messageIndex, 1);

    const updatedThread: ChatThreadWithMessages = {
      ...existingThread,
      messages: updatedMessages,
    };

    const newThreads = [...threads];
    newThreads[threadIndex] = updatedThread;

    set({ threads: newThreads });

    // Drop the message from the persisted room-messages query cache too.
    mutateRoomQueryMessages(threadId, (m) => (m.id === messageId ? null : m));

    return true;
  },
  
  /**
   * Update a message in a thread by unique_message_id.
   * 
   * Used for optimistic updates - finds message by unique_message_id and applies partial updates.
   * 
   * @param threadId - Thread ID (room_id from API) containing the message
   * @param uniqueMessageId - Unique message ID to find
   * @param updates - Partial message updates to apply
   * @returns true if message was found and updated, false if thread/message not found
   * 
   * @example
   * ```typescript
   * // Update upload status on a message
   * updateMsg('chat_123', 'unique_id_456', { 
   *   media_object: { ...existingMediaObject, uploadStatus: 'uploaded' }
   * });
   * ```
   */
  updateMsg: (threadId, uniqueMessageId, updates) => {
    const { threads } = get();
    const threadIndex = threads.findIndex(t => t.meta.room_id === threadId);

    if (threadIndex === -1) {
      return false;
    }

    const existingThread = threads[threadIndex];
    const messageIndex = existingThread.messages.findIndex(
      msg => msg.unique_message_id === uniqueMessageId
    );

    if (messageIndex === -1) {
      return false;
    }
    
    const existingMessage = existingThread.messages[messageIndex];
    const updatedMessage = { ...existingMessage, ...updates };
    
    // If media_object is being updated, merge it properly
    if (updates.media_object && existingMessage.media_object) {
      updatedMessage.media_object = {
        ...existingMessage.media_object,
        ...updates.media_object,
      };
    }
    
    const updatedMessages = [...existingThread.messages];
    updatedMessages[messageIndex] = updatedMessage;
    
    const updatedThread: ChatThreadWithMessages = {
      ...existingThread,
      messages: updatedMessages,
    };
    
    const newThreads = [...threads];
    newThreads[threadIndex] = updatedThread;

    set({ threads: newThreads });

    // Mirror the update into the persisted room-messages query cache.
    mutateRoomQueryMessages(threadId, (m) =>
      m.unique_message_id === uniqueMessageId ? updatedMessage : m,
    );

    // A positive server id means the server has accepted this message —
    // it is confirmed, so drop it from the retry outbox.
    if (typeof updates.id === 'number' && updates.id > 0) {
      useChatOutboxStore.getState().remove(uniqueMessageId);
    }

    return true;
  },
  
  setReaction: (threadId, chatId, userId, reaction) => {
    const { threads } = get();
    const threadIndex = threads.findIndex((t) => t.meta.room_id === threadId);
    if (threadIndex === -1) return false;

    const existingThread = threads[threadIndex];
    const messageIndex = existingThread.messages.findIndex((m) => m.id === chatId);
    if (messageIndex === -1) return false;

    const existingMessage = existingThread.messages[messageIndex];
    const statuses = Array.isArray(existingMessage.message_status)
      ? existingMessage.message_status
      : [];

    const newReaction = reaction && reaction.length > 0 ? reaction : null;
    const reactionTime = newReaction ? String(Date.now()) : null;

    // Try to find an existing message_status row for this user.
    const statusIndex = statuses.findIndex((s) => s.user_id === userId);
    let updatedStatuses;
    if (statusIndex >= 0) {
      updatedStatuses = statuses.map((s, i) =>
        i === statusIndex ? { ...s, reaction: newReaction, reaction_time: reactionTime } : s,
      );
    } else {
      // No row yet (e.g. group member who hasn't received the message status
      // entry from the server) — synthesize one so the UI can show the chip
      // immediately. Server will overwrite this on next sync.
      updatedStatuses = [
        ...statuses,
        {
          id: 0,
          user_id: userId,
          read_time: '0',
          received_time: '0',
          reaction: newReaction,
          reaction_time: reactionTime,
          call_status: null,
          call_time: null,
          chat_id: chatId,
        },
      ];
    }

    const updatedMessage: RawChatMessage = {
      ...existingMessage,
      message_status: updatedStatuses,
    };
    const updatedMessages = [...existingThread.messages];
    updatedMessages[messageIndex] = updatedMessage;

    const updatedThread: ChatThreadWithMessages = {
      ...existingThread,
      messages: updatedMessages,
    };
    const newThreads = [...threads];
    newThreads[threadIndex] = updatedThread;
    set({ threads: newThreads });

    // Mirror the reaction change into the persisted room-messages query cache.
    mutateRoomQueryMessages(threadId, (m) => (m.id === chatId ? updatedMessage : m));

    return true;
  },

  setMessageStatus: (chatId, userId, fields) => {
    if (!Number.isFinite(chatId) || chatId <= 0) return false;
    if (!Number.isFinite(userId) || userId <= 0) return false;

    // Coerce timestamps to the server's string-epoch format so they roundtrip
    // cleanly through deriveMessageStatus (which Number()s the field). Accept
    // both unix seconds and ms — the rest of the codebase auto-detects.
    const stamp = (v: string | number | undefined): string | undefined => {
      if (v === undefined) return undefined;
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return String(n);
    };
    const nextReceived = stamp(fields.received_time);
    const nextRead = stamp(fields.read_time);
    if (!nextReceived && !nextRead) return false;

    const { threads } = get();
    let foundThreadIndex = -1;
    let foundMessageIndex = -1;
    for (let i = 0; i < threads.length; i++) {
      const idx = threads[i].messages.findIndex((m) => m.id === chatId);
      if (idx >= 0) {
        foundThreadIndex = i;
        foundMessageIndex = idx;
        break;
      }
    }
    if (foundThreadIndex < 0) return false;

    const thread = threads[foundThreadIndex];
    const message = thread.messages[foundMessageIndex];
    const statuses = Array.isArray(message.message_status)
      ? message.message_status
      : [];

    const idx = statuses.findIndex((s) => s.user_id === userId);
    let updatedStatuses;
    if (idx >= 0) {
      // Don't regress a timestamp — once read, the receipt stays read even if
      // a stale "received" event arrives later.
      const cur = statuses[idx];
      updatedStatuses = statuses.map((s, i) =>
        i === idx
          ? {
              ...s,
              received_time:
                nextReceived &&
                Number(nextReceived) > Number(cur.received_time ?? '0')
                  ? nextReceived
                  : cur.received_time,
              read_time:
                nextRead && Number(nextRead) > Number(cur.read_time ?? '0')
                  ? nextRead
                  : cur.read_time,
            }
          : s,
      );
    } else {
      updatedStatuses = [
        ...statuses,
        {
          id: 0,
          user_id: userId,
          received_time: nextReceived ?? '0',
          read_time: nextRead ?? '0',
          reaction: null,
          reaction_time: null,
          call_status: null,
          call_time: null,
          chat_id: chatId,
        },
      ];
    }

    const updatedMessage: RawChatMessage = {
      ...message,
      message_status: updatedStatuses,
    };
    const updatedMessages = [...thread.messages];
    updatedMessages[foundMessageIndex] = updatedMessage;
    const updatedThread: ChatThreadWithMessages = {
      ...thread,
      messages: updatedMessages,
    };
    const nextThreads = [...threads];
    nextThreads[foundThreadIndex] = updatedThread;
    set({ threads: nextThreads });

    // NOTE: deliberately does NOT mirror into the room-messages query cache.
    // Receipts are high-frequency (a burst on every thread open) and the
    // store is the display source of truth; writing each one back via
    // setQueryData re-fired the `pages → mergeMessages` sync and rebuilt the
    // entire cached history per receipt, which is what OOM'd heavy threads.
    // The query is no longer persisted, so it simply refetches fresh status
    // on the next mount/focus.

    return true;
  },

  setMessageStatuses: (updates) => {
    if (!Array.isArray(updates) || updates.length === 0) return false;

    const stamp = (v: string | number | undefined): string | undefined => {
      if (v === undefined) return undefined;
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return String(n);
    };

    // Group every receipt by the message it targets so one message updated by
    // several receipts in the same burst is touched once.
    const byChat = new Map<
      number,
      Array<{ userId: number; received?: string; read?: string }>
    >();
    for (const u of updates) {
      if (!Number.isFinite(u.chatId) || u.chatId <= 0) continue;
      if (!Number.isFinite(u.userId) || u.userId <= 0) continue;
      const received = stamp(u.received_time);
      const read = stamp(u.read_time);
      if (!received && !read) continue;
      const list = byChat.get(u.chatId) ?? [];
      list.push({ userId: u.userId, received, read });
      byChat.set(u.chatId, list);
    }
    if (byChat.size === 0) return false;

    // Apply this burst's receipts to a single message. Returns the SAME object
    // when nothing changed (preserves identity for MessageRow memo + lets us
    // detect "no-op" rooms). Shared by the store update and the query mirror.
    const applyToMessage = (message: RawChatMessage): RawChatMessage => {
      const ups = byChat.get(message.id);
      if (!ups) return message;

      let statuses = Array.isArray(message.message_status)
        ? message.message_status
        : [];

      for (const up of ups) {
        const idx = statuses.findIndex((s) => s.user_id === up.userId);
        if (idx >= 0) {
          // Don't regress a timestamp — once read, the receipt stays read even
          // if a stale "received" event lands later.
          const cur = statuses[idx];
          const nextReceived =
            up.received && Number(up.received) > Number(cur.received_time ?? '0')
              ? up.received
              : cur.received_time;
          const nextRead =
            up.read && Number(up.read) > Number(cur.read_time ?? '0')
              ? up.read
              : cur.read_time;
          if (nextReceived === cur.received_time && nextRead === cur.read_time) {
            continue; // nothing new for this user
          }
          statuses = statuses.map((s, i) =>
            i === idx
              ? { ...s, received_time: nextReceived, read_time: nextRead }
              : s,
          );
        } else {
          statuses = [
            ...statuses,
            {
              id: 0,
              user_id: up.userId,
              received_time: up.received ?? '0',
              read_time: up.read ?? '0',
              reaction: null,
              reaction_time: null,
              call_status: null,
              call_time: null,
              chat_id: message.id,
            },
          ];
        }
      }

      if (statuses === message.message_status) return message;
      return { ...message, message_status: statuses };
    };

    const { threads } = get();
    let changed = false;
    const changedRoomIds: string[] = [];
    const nextThreads = threads.map((thread) => {
      let threadChanged = false;
      const nextMessages = thread.messages.map((message) => {
        const next = applyToMessage(message);
        if (next !== message) threadChanged = true;
        return next;
      });

      if (!threadChanged) return thread;
      changed = true;
      changedRoomIds.push(thread.meta.room_id);
      return { ...thread, messages: nextMessages };
    });

    if (!changed) return false;
    set({ threads: nextThreads });

    // Mirror into the room-messages query window (the open thread's display
    // source). One pass per affected room; bounded by WINDOW_MAX_PAGES pages.
    for (const roomId of changedRoomIds) {
      mutateRoomQueryMessages(roomId, applyToMessage);
    }

    return true;
  },

  setReplyDraft: (threadId, draft) => {
    const next = new Map(get().replyDrafts);
    next.set(threadId, draft);
    set({ replyDrafts: next });
  },

  clearReplyDraft: (threadId) => {
    const cur = get().replyDrafts;
    if (!cur.has(threadId)) return;
    const next = new Map(cur);
    next.delete(threadId);
    set({ replyDrafts: next });
  },

  /**
   * Set hydration state.
   * 
   * Used to track when chat data is being loaded initially.
   * 
   * @param isHydrating - true when hydrating, false when done
   * 
   * @example
   * ```typescript
   * setIsHydrating(true);  // Start hydration
   * setIsHydrating(false); // Hydration complete
   * ```
   */
  setIsHydrating: (isHydrating) => set({ isHydrating }),
  
  /**
   * Clear all chat state and reset to initial values.
   * 
   * Useful for logout or when user wants to clear chat data.
   * Resets selected chat, typing users, online users, cached threads, and hydration state.
   */
  clearChat: () => set({
    selectedThread: null,
    typingUsers: new Map(),
    onlineUsers: new Map(),
    lastSeenMap: new Map(),
    replyDrafts: new Map(),
    threads: [],
    isHydrating: false,
    threadsNextPage: 2,
    threadsHasMore: true,
    threadsLoadingMore: false,
  }),
    }),
    {
      name: CHAT_STORE_PERSIST_KEY,
      version: 1,
      storage: createJSONStorage(() => idbStateStorage),
      // Only the durable conversation data is persisted. Typing/presence/reply
      // drafts are ephemeral, and `isHydrating` is a transient loading flag —
      // none of them should survive a reload.
      //
      // `selectedThread` is deliberately NOT persisted either. It answers "is
      // the user looking at this room right now", and both readers — the unread
      // badge in `insertMsg`/`mergeMessages` and the notification suppressor in
      // `buildChatNotification` — suppress on a match. The route sets it on
      // mount and clears it on unmount, but an unmount cleanup does not run
      // when the tab is closed, so a persisted value came back on the next cold
      // start still naming the last room open. While the user sat anywhere else,
      // that room silently took no badge and raised no notification until they
      // opened some thread. Nothing needs it to outlive the session.
      partialize: (state) => ({
        // Persist only the most-recent slice of threads. Heavy accounts that
        // accumulate hundreds of conversations would otherwise rehydrate the
        // whole list into memory on cold start, defeating the lazy backfill.
        // Older threads stream back in via `useThreadListBackfill` on scroll.
        threads: state.threads.slice(0, 50).map((thread) => ({
          ...thread,
          // Drop optimistic/unsent messages (negative id — there is no outbox
          // yet) and keep only the newest page so the cache stays small.
          messages: thread.messages
            .filter((m) => Number(m.id) > 0)
            .slice(0, MAX_PERSISTED_MESSAGES),
        })),
      }),
    },
  ),
);

